import { useCallback } from 'react'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecord } from '@/shared/hooks'
import { api } from '@/shared/lib/api'
import type { MissionCapacity } from '@/types/mission'

const EMPTY_CAPACITY: MissionCapacity[] = []

export const useMissionCapacity = () => {
  const { digitalProductId } = useDigitalProductContext()

  const {
    record,
    refetch,
    isLoading
  } = useRecord({
    r: `/user/digital_products/${digitalProductId}/missions`,
    action: 'capacity',
    forceIdPresence: false,
    shouldFetch: !!digitalProductId,
    options: {
      refetchInterval: 60000,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true
    },
    queryFn: async ({ queryKey }) => {
      const { data } = await api.get(queryKey[0])

      return { record: data }
    }
  }) as {
    record?: { records?: MissionCapacity[] }
    refetch: () => void
    isLoading: boolean
  }

  const missionCapacity = record?.records ?? EMPTY_CAPACITY

  const normalizeId = (value?: number | string | null) =>
    value === null || typeof value === 'undefined' ? '' : String(value)

  const getCapacityByMissionId = useCallback(
    (missionId?: number) =>
      missionCapacity.find(
        capacity =>
          normalizeId(capacity.mission_id) === normalizeId(missionId)
      ),
    [missionCapacity]
  )

  const getCapacityByMissionUid = useCallback(
    (missionUid?: string) =>
      missionCapacity.find(
        capacity =>
          normalizeId(capacity.mission_uid) === normalizeId(missionUid)
      ),
    [missionCapacity]
  )

  return {
    missionCapacity,
    getCapacityByMissionId,
    getCapacityByMissionUid,
    refetchMissionCapacity: refetch,
    isLoadingMissionCapacity: isLoading
  }
}
